import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import API from '../../utils/api';
import { printBill, downloadBill, printPayment, downloadPayment } from '../../utils/pdf';
import { exportToCSV, exportToPDF } from '../../utils/reportsExport';
import toast from 'react-hot-toast';

const formatAddress = (addressString) => {
  if (!addressString) return 'No Address';
  try {
    const parsed = JSON.parse(addressString);
    const parts = [];
    if (parsed.street) parts.push(parsed.street);
    if (parsed.city) parts.push(parsed.city);
    if (parsed.pincode) parts.push(parsed.pincode);
    return parts.length > 0 ? parts.join(', ') : 'No Address';
  } catch (e) {
    return addressString;
  }
};

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedData, setSelectedData] = useState(null);
  const [modal, setModal] = useState(''); // 'add-customer' | 'edit-customer' | 'add-payment'
  const [form, setForm] = useState({});
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const [savingCustomer, setSavingCustomer] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);

  const [editingCust, setEditingCust] = useState(null);
  const [confirmDeleteCust, setConfirmDeleteCust] = useState(null);
  const [deletingCust, setDeletingCust] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [confirmDeletePayment, setConfirmDeletePayment] = useState(null);
  const [deletingPayment, setDeletingPayment] = useState(false);

  // Sub-tabs for customer details
  const [detailTab, setDetailTab] = useState('invoices'); // 'invoices' | 'payments'

  const openEditCustomer = (c) => {
    setEditingCust(c);
    let parsedAddress = { street: '', city: '', pincode: '' };
    try {
      if (c.address) {
        parsedAddress = JSON.parse(c.address);
      }
    } catch(e) {}
    setForm({
      name: c.name,
      phone: c.phone,
      email: c.email || '',
      street: parsedAddress.street || '',
      city: parsedAddress.city || '',
      pincode: parsedAddress.pincode || ''
    });
    setModal('edit-customer');
  };

  const handleDeleteCustomer = async (id) => {
    if (deletingCust) return;
    setDeletingCust(true);
    try {
      await API.delete(`/customers/${id}`);
      toast.success('Customer deleted successfully!');
      setSelected(null);
      setSelectedData(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete customer. They may have active invoices.');
    } finally {
      setDeletingCust(false);
    }
  };

  const fetchAll = () => {
    API.get('/customers')
      .then(r => setCustomers(r.data))
      .catch(() => toast.error('Failed to load customers'))
      .finally(() => setLoading(false));
  };

  const fetchOne = (id) => {
    API.get(`/customers/${id}`)
      .then(r => setSelectedData(r.data))
      .catch(() => toast.error('Failed to load customer details'));
  };

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    if (selected) {
      fetchOne(selected.id);
    } else {
      setSelectedData(null);
    }
  }, [selected]);

  const saveCustomer = async () => {
    if (savingCustomer) return;
    if (!form.name || !form.phone) return toast.error('Name and phone required');
    setSavingCustomer(true);
    try {
      const addressJson = JSON.stringify({
        street: form.street || '',
        city: form.city || '',
        pincode: form.pincode || ''
      });
      const customerData = {
        name: form.name,
        phone: form.phone,
        email: form.email || '',
        address: addressJson
      };
      if (modal === 'edit-customer') {
        await API.put(`/customers/${editingCust.id}`, customerData);
        toast.success('Customer updated successfully');
      } else {
        await API.post('/customers', customerData);
        toast.success('Customer registered successfully');
      }
      fetchAll();
      if (selected && selected.id === editingCust?.id) {
        fetchOne(selected.id);
      }
      setModal('');
      setForm({});
      setEditingCust(null);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save customer');
    } finally {
      setSavingCustomer(false);
    }
  };

  const openEditPayment = (p) => {
    setEditingPayment(p);
    setForm({
      amountPaid: String(p.amountPaid),
      date: p.date ? new Date(p.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      mode: p.mode
    });
    setModal('edit-payment');
  };

  const savePayment = async () => {
    if (savingPayment) return;
    if (!form.amountPaid || isNaN(form.amountPaid) || parseFloat(form.amountPaid) <= 0) {
      return toast.error('Valid payment amount required');
    }
    setSavingPayment(true);
    try {
      const payload = {
        amountPaid: parseFloat(form.amountPaid),
        date: form.date,
        mode: form.mode
      };
      if (modal === 'edit-payment') {
        await API.put(`/customers/payments/${editingPayment.id}`, payload);
        toast.success('Payment updated successfully');
      } else {
        await API.post(`/customers/${selected.id}/payments`, payload);
        toast.success('Payment recorded successfully');
      }
      fetchAll();
      fetchOne(selected.id);
      setModal('');
      setForm({});
      setEditingPayment(null);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save payment');
    } finally {
      setSavingPayment(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (deletingPayment) return;
    setDeletingPayment(true);
    try {
      await API.delete(`/customers/payments/${paymentId}`);
      toast.success('Payment deleted successfully');
      fetchAll();
      fetchOne(selected.id);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to delete payment');
    } finally {
      setDeletingPayment(false);
    }
  };

  const filtered = customers.filter(c => 
    c.name.toLowerCase().includes(search.toLowerCase()) || 
    c.phone.includes(search)
  );

  // Export all customers with outstanding due
  const handleExportCustomers = (format) => {
    const headers = ['Customer Name', 'Phone Number', 'Outstanding Due (Rs.)'];
    const rows = customers.map(c => [
      c.name,
      c.phone,
      c.balance > 0 ? c.balance.toFixed(2) : '-'
    ]);
    const totalDue = customers.reduce((sum, c) => sum + (c.balance > 0 ? c.balance : 0), 0);

    const date = new Date().toISOString().split('T')[0];
    if (format === 'csv') {
      // Add total row at bottom for CSV
      const csvRows = [...rows, ['', 'Total Outstanding Due', totalDue.toFixed(2)]];
      exportToCSV(headers, csvRows, `customers_outstanding_${date}.csv`);
      toast.success('Excel CSV downloaded');
    } else {
      // Pass custom footer for PDF — no extra data row, clean footer bar
      exportToPDF(
        'Customer Outstanding Due Report',
        headers,
        rows,
        `customers_outstanding_${date}.pdf`,
        {
          customFooter: ['Total Outstanding Due', '', `Rs. ${totalDue.toFixed(2)}`],
          boldLastColumn: true
        }
      );
      toast.success('PDF downloaded');
    }
  };


  return (
    <Layout title="Customer Accounts & Ledger" actions={<button className="btn btn-gold" onClick={() => { setForm({}); setModal('add-customer'); }}>+ Add Customer</button>}>
      <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 1.2fr' : '1fr', gap: 24 }}>
        {/* Customer List */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">All Customers ({filtered.length})</span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <div className="search-bar" style={{ width: 220 }}>
                <span className="search-icon">🔍</span>
                <input className="form-control" placeholder="Search customer..." value={search} onChange={e => setSearch(e.target.value)} style={{ paddingLeft: 36 }} />
              </div>
              <button className="btn btn-outline" style={{ padding: '6px 12px', fontSize: '0.8rem', whiteSpace: 'nowrap' }} onClick={() => handleExportCustomers('csv')}>
                📥 Excel
              </button>
              <button className="btn btn-primary" style={{ padding: '6px 12px', fontSize: '0.8rem', whiteSpace: 'nowrap' }} onClick={() => handleExportCustomers('pdf')}>
                🖨️ PDF
              </button>
            </div>
          </div>
          <div className="table-wrap">
            {loading ? <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-400)' }}>Loading...</div> :
              filtered.length === 0 ? <div className="empty-state"><div className="empty-icon">👥</div><div className="empty-title">No customers</div></div> : (
                <table>
                  <thead><tr><th>Name</th><th>Phone</th><th>Outstanding Due</th><th></th></tr></thead>
                  <tbody>
                    {filtered.map(c => (
                      <tr key={c.id} style={{ cursor: 'pointer', background: selected?.id === c.id ? 'var(--gold-pale)' : '' }}
                        onClick={() => setSelected(c)}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--navy)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.8rem', flexShrink: 0 }}>{c.name[0]}</div>
                            <div>
                              <div style={{ fontWeight: 600, color: 'var(--navy)' }}>{c.name}</div>
                              <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>{c.email || 'No Email'}</div>
                            </div>
                          </div>
                        </td>
                        <td>{c.phone}</td>
                        <td>
                          {c.balance > 0 ? <span className="badge badge-red">₹{c.balance.toFixed(2)} due</span>
                            : c.totalDue > 0 ? <span className="badge badge-green">Fully Paid</span>
                              : <span className="badge badge-gray">—</span>}
                        </td>
                        <td><span style={{ color: 'var(--navy)', fontSize: '1.1rem' }}>›</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
          </div>
        </div>

        {/* Customer Detail & Transactions */}
        {selected && selectedData && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Profile Overview */}
            <div className="card">
              <div className="card-header">
                <div>
                  <div className="card-title">{selectedData.name}</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)', marginTop: 2 }}>{selectedData.phone} · {formatAddress(selectedData.address)}</div>
                </div>
                <button className="modal-close" onClick={() => { setSelected(null); setSelectedData(null); }} style={{ fontSize: '1.3rem' }}>×</button>
              </div>
              <div className="card-body" style={{ paddingTop: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
                  {[
                    { l: 'Total Invoiced', v: `₹${selectedData.totalDue?.toFixed(2)}` },
                    { l: 'Total Paid', v: `₹${selectedData.totalPaid?.toFixed(2)}` },
                    { l: 'Outstanding Due', v: `₹${selectedData.balance?.toFixed(2)}` },
                  ].map(s => (
                    <div key={s.l} style={{ background: 'var(--gray-50)', padding: '12px 14px', borderRadius: 'var(--radius-md)', border: '1px solid var(--gray-100)' }}>
                      <div style={{ fontSize: '0.7rem', color: 'var(--gray-500)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.l}</div>
                      <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', color: s.l === 'Outstanding Due' && selectedData.balance > 0 ? 'var(--red)' : 'var(--navy)', marginTop: 3, fontWeight: 700 }}>{s.v}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {selectedData.balance > 0 && (
                    <button className="btn btn-success btn-sm" onClick={() => { setForm({ amountPaid: selectedData.balance.toFixed(2), date: new Date().toISOString().split('T')[0], mode: 'cash' }); setModal('add-payment'); }}>
                      Record Customer Payment
                    </button>
                  )}
                  <button className="btn btn-outline btn-sm" onClick={() => openEditCustomer(selectedData)}>✏️ Edit Profile</button>
                  <button className="btn btn-danger btn-sm" onClick={() => setConfirmDeleteCust(selectedData)}>🗑️ Delete Customer</button>
                </div>
              </div>
            </div>

            {/* Invoices & Payments Lists */}
            <div className="card fade-in">
              <div className="card-header" style={{ paddingBottom: 0 }}>
                <div style={{ display: 'flex', gap: 12 }}>
                  <button className={`btn btn-sm ${detailTab === 'invoices' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setDetailTab('invoices')}>
                    🧾 Bills / Invoices ({selectedData.bills?.length || 0})
                  </button>
                  <button className={`btn btn-sm ${detailTab === 'payments' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setDetailTab('payments')}>
                    💳 Customer Payments ({selectedData.payments?.length || 0})
                  </button>
                </div>
              </div>
              
              <div className="table-wrap" style={{ paddingTop: 10 }}>
                {detailTab === 'invoices' ? (
                  (selectedData.bills || []).length === 0 ? (
                    <div style={{ padding: 30, textAlign: 'center', color: 'var(--gray-400)' }}>No invoices generated for this customer</div>
                  ) : (
                    <table>
                      <thead><tr><th>Invoice No</th><th>Date</th><th>Amount</th><th>Mode</th><th>Actions</th></tr></thead>
                      <tbody>
                        {selectedData.bills.map(b => (
                          <tr key={b.id}>
                            <td><strong>{b.invoiceNo}</strong></td>
                            <td>{new Date(b.createdAt).toLocaleDateString('en-IN')}</td>
                            <td style={{ fontWeight: 600 }}>₹{b.total.toFixed(2)}</td>
                             <td><span className={`badge ${b.paymentMode === 'cash' ? 'badge-green' : b.paymentMode === 'card' ? 'badge-navy' : b.paymentMode === 'credit' ? 'badge-red' : b.paymentMode === 'discount' ? 'badge-amber' : 'badge-gray'}`}>{b.paymentMode}</span></td>
                              <td>
                                <div style={{ display: 'flex', gap: 4 }}>
                                  <button className="btn btn-outline btn-sm" onClick={() => downloadBill({ ...b, customer: selectedData })}>⬇️</button>
                                  <button className="btn btn-primary btn-sm" onClick={() => printBill({ ...b, customer: selectedData })}>🖨️</button>
                                </div>
                              </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )
                ) : (
                  (selectedData.payments || []).length === 0 ? (
                    <div style={{ padding: 30, textAlign: 'center', color: 'var(--gray-400)' }}>No payments logged yet</div>
                  ) : (
                    <table>
                      <thead><tr><th>Date</th><th>Payment Mode</th><th style={{ textAlign: 'right' }}>Amount Paid</th><th style={{ textAlign: 'right', width: 100 }}>Actions</th></tr></thead>
                      <tbody>
                        {selectedData.payments.map(p => (
                          <tr key={p.id}>
                            <td>{new Date(p.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                             <td><span className={`badge ${p.mode === 'cash' ? 'badge-green' : p.mode === 'card' ? 'badge-navy' : p.mode === 'discount' ? 'badge-red' : 'badge-amber'}`}>{p.mode}</span></td>
                            <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--green)' }}>₹{p.amountPaid.toFixed(2)}</td>
                            <td style={{ textAlign: 'right' }}>
                              <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                                <button className="btn btn-outline btn-sm" onClick={() => downloadPayment(p, selectedData)} title="Download Receipt">⬇️</button>
                                <button className="btn btn-primary btn-sm" onClick={() => printPayment(p, selectedData)} title="Print Receipt">🖨️</button>
                                <button className="btn btn-success btn-sm" onClick={() => openEditPayment(p)} title="Edit Payment">✏️</button>
                                <button className="btn btn-danger btn-sm" onClick={() => setConfirmDeletePayment(p)} title="Delete Payment">🗑️</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {(modal === 'add-customer' || modal === 'edit-customer') && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal('')}>
          <div className="modal">
            <div className="modal-header"><span className="modal-title">{modal === 'edit-customer' ? 'Edit Customer Details' : 'Register Customer'}</span><button className="modal-close" onClick={() => setModal('')}>×</button></div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group"><label className="form-label">Full Name *</label><input className="form-control" value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Customer Name" /></div>
                <div className="form-group"><label className="form-label">Phone *</label><input className="form-control" value={form.phone || ''} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="Mobile Number" /></div>
              </div>
              <div className="form-group"><label className="form-label">Email Address</label><input className="form-control" type="email" value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="customer@example.com" /></div>
              <div className="form-group">
                <label className="form-label">Street Name</label>
                <input 
                  className="form-control" 
                  value={form.street || ''} 
                  onChange={e => setForm({ ...form, street: e.target.value })} 
                  placeholder="Street name / Door No." 
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">City</label>
                  <input 
                    className="form-control" 
                    value={form.city || ''} 
                    onChange={e => setForm({ ...form, city: e.target.value })} 
                    placeholder="City" 
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Pincode</label>
                  <input 
                    className="form-control" 
                    value={form.pincode || ''} 
                    onChange={e => setForm({ ...form, pincode: e.target.value })} 
                    placeholder="Pincode" 
                  />
                </div>
              </div>
            </div>
             <div className="modal-footer"><button className="btn btn-outline" onClick={() => setModal('')} disabled={savingCustomer}>Cancel</button><button className="btn btn-primary" onClick={saveCustomer} disabled={savingCustomer}>{savingCustomer ? '⏳ Saving...' : (modal === 'edit-customer' ? 'Update Customer' : 'Register Customer')}</button></div>
          </div>
        </div>
      )}

      {(modal === 'add-payment' || modal === 'edit-payment') && selected && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal('')}>
          <div className="modal">
            <div className="modal-header"><span className="modal-title">{modal === 'edit-payment' ? `Edit Payment: ${selected.name}` : `Record Payment: ${selected.name}`}</span><button className="modal-close" onClick={() => setModal('')}>×</button></div>
            <div className="modal-body">
              <div style={{ background: 'var(--red-light)', border: '1px solid #fbc2bc', borderRadius: 'var(--radius-sm)', padding: '12px 14px', marginBottom: 18, fontSize: '0.85rem', color: 'var(--red)' }}>
                Outstanding Debt remaining: <strong>₹{selectedData?.balance?.toFixed(2)}</strong>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Amount Paid (₹) *</label><input className="form-control" type="number" min="0.01" step="0.01" value={form.amountPaid || ''} onChange={e => setForm({ ...form, amountPaid: e.target.value })} placeholder="Enter amount" /></div>
                <div className="form-group"><label className="form-label">Payment Date</label><input className="form-control" type="date" value={form.date || ''} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
              </div>
              <div className="form-group"><label className="form-label">Payment Mode</label>
                <select className="form-control" value={form.mode || 'cash'} onChange={e => setForm({ ...form, mode: e.target.value })}>
                  <option value="cash">Cash</option><option value="card">Card</option><option value="upi">UPI (GPay/PhonePe)</option><option value="net_banking">Net Banking</option><option value="discount">Discount / Wave Off</option>
                </select>
              </div>
            </div>
             <div className="modal-footer"><button className="btn btn-outline" onClick={() => setModal('')} disabled={savingPayment}>Cancel</button><button className="btn btn-success" onClick={savePayment} disabled={savingPayment}>{savingPayment ? '⏳ Saving...' : (modal === 'edit-payment' ? 'Save Changes' : 'Record Payment')}</button></div>
          </div>
        </div>
      )}

      {confirmDeleteCust && (
        <div className="confirm-overlay" onClick={() => setConfirmDeleteCust(null)}>
          <div className="confirm-dialog" onClick={e => e.stopPropagation()}>
            <div className="confirm-icon">⚠️</div>
            <div className="confirm-title">Delete Customer?</div>
            <div className="confirm-desc">
              Are you sure you want to permanently delete customer <strong>{confirmDeleteCust.name}</strong>?
              This will also delete their payment receipts history.
            </div>
            <div className="confirm-buttons">
              <button className="btn btn-outline" onClick={() => setConfirmDeleteCust(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={() => {
                const id = confirmDeleteCust.id;
                setConfirmDeleteCust(null);
                handleDeleteCustomer(id);
              }} disabled={deletingCust}>
                {deletingCust ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
      {confirmDeletePayment && (
        <div className="confirm-overlay" onClick={() => setConfirmDeletePayment(null)}>
          <div className="confirm-dialog" onClick={e => e.stopPropagation()}>
            <div className="confirm-icon">⚠️</div>
            <div className="confirm-title">Delete Payment Log?</div>
            <div className="confirm-desc">
              Are you sure you want to permanently delete this payment log of <strong>₹{confirmDeletePayment.amountPaid.toFixed(2)}</strong>?
              This will increase the customer's outstanding balance accordingly.
            </div>
            <div className="confirm-buttons">
              <button className="btn btn-outline" onClick={() => setConfirmDeletePayment(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={() => {
                const id = confirmDeletePayment.id;
                setConfirmDeletePayment(null);
                handleDeletePayment(id);
              }} disabled={deletingPayment}>
                {deletingPayment ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
